const MULTI_PART_SUFFIXES = new Set([
    'com.sa', 'com.ae', 'com.eg', 'com.kw', 'com.bh', 'com.om', 'com.qa',
    'co.uk', 'org.uk', 'ac.uk', 'gov.uk',
    'com.au', 'net.au', 'org.au',
    'co.nz', 'co.jp', 'co.kr', 'co.in', 'co.za', 'co.il', 'co.id',
    'com.br', 'com.mx', 'com.ar', 'com.tr', 'com.tw', 'com.hk',
    'com.sg', 'com.my', 'com.ph', 'com.vn', 'com.ng', 'com.pk',
    'com.pe', 'com.co', 'com.pl', 'gov.sa',
]);

const AI_HOST_TO_SOURCE: Record<string, string> = {
    'chatgpt.com': 'chatgpt',
    'chat.openai.com': 'chatgpt',
    'openai.com': 'chatgpt',
    'perplexity.ai': 'perplexity',
    'gemini.google.com': 'gemini',
    'bard.google.com': 'gemini',
    'claude.ai': 'claude',
    'chat.deepseek.com': 'deepseek',
    'deepseek.com': 'deepseek',
    'copilot.microsoft.com': 'copilot',
    'grok.com': 'grok',
    'x.ai': 'grok',
};

const SEARCH_HOST_TO_SOURCE: Record<string, string> = {
    'bing.com': 'bing',
    'duckduckgo.com': 'duckduckgo',
    'search.yahoo.com': 'yahoo',
    'yahoo.com': 'yahoo',
    'yandex.com': 'yandex',
    'yandex.ru': 'yandex',
    'search.brave.com': 'brave',
    'ecosia.org': 'ecosia',
    'baidu.com': 'baidu',
    'startpage.com': 'startpage',
};

const SOCIAL_HOST_TO_SOURCE: Record<string, string> = {
    'facebook.com': 'facebook',
    'instagram.com': 'instagram',
    'linkedin.com': 'linkedin',
    'twitter.com': 'twitter',
    'x.com': 'twitter',
    't.co': 'twitter',
    'tiktok.com': 'tiktok',
    'snapchat.com': 'snapchat',
    'youtube.com': 'youtube',
    'reddit.com': 'reddit',
    'wa.me': 'whatsapp',
    'web.whatsapp.com': 'whatsapp',
    't.me': 'telegram',
};

const GOOGLE_SEARCH_HOST = /^google\.(com|[a-z]{2}|com\.[a-z]{2}|co\.[a-z]{2})$/;

const LLM_SOURCE_ALIASES: Record<string, string> = {
    chatgpt: 'chatgpt',
    'chatgpt.com': 'chatgpt',
    'chat.openai.com': 'chatgpt',
    'openai.com': 'chatgpt',
    openai: 'chatgpt',
    perplexity: 'perplexity',
    'perplexity.ai': 'perplexity',
    gemini: 'gemini',
    'gemini.google.com': 'gemini',
    bard: 'gemini',
    'bard.google.com': 'gemini',
    claude: 'claude',
    'claude.ai': 'claude',
    deepseek: 'deepseek',
    'deepseek.com': 'deepseek',
    'chat.deepseek.com': 'deepseek',
    copilot: 'copilot',
    'copilot.microsoft.com': 'copilot',
    grok: 'grok',
    'grok.com': 'grok',
    'x.ai': 'grok',
};

export type ReferrerClass =
    | { kind: 'empty' }
    | { kind: 'unparseable' }
    | { kind: 'internal'; hostname: string }
    | { kind: 'ai'; source: string; hostname: string }
    | { kind: 'search'; source: string; hostname: string }
    | { kind: 'social'; source: string; hostname: string }
    | { kind: 'referral'; source: string; hostname: string };

export function normalizeHostname(raw: string): string {
    return raw.toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
}

export function registrableDomain(hostname: string): string {
    const parts = hostname.split('.').filter(Boolean);
    if (parts.length <= 2) return hostname;
    const lastTwo = parts.slice(-2).join('.');
    if (MULTI_PART_SUFFIXES.has(lastTwo) && parts.length >= 3) {
        return parts.slice(-3).join('.');
    }
    return lastTwo;
}

/** Dev/preview hosts. Ports are not part of a cookie's identity, so localhost:3001 and
 *  localhost:4200 share one cookie jar and must not look like a referral to each other. */
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);

export function isLocalHostname(hostname: string): boolean {
    return LOCAL_HOSTNAMES.has(normalizeHostname(hostname));
}

/** sabbar.com, app.sabbar.com, and the giglaya staging hosts. */
export function isSabbarHostname(hostname: string | null | undefined): boolean {
    if (!hostname) return false;
    const host = normalizeHostname(hostname);
    return host === 'sabbar.com' || host.endsWith('.sabbar.com')
        || host === 'giglaya.com' || host.endsWith('.giglaya.com');
}

function currentHostname(): string | null {
    if (typeof window === 'undefined') return null;
    return normalizeHostname(window.location.hostname || '');
}

export function isInternalRegistrableDomain(hostname: string, current: string | null = currentHostname()): boolean {
    if (LOCAL_HOSTNAMES.has(hostname)) return true;
    const root = registrableDomain(hostname);
    if (root === 'sabbar.com' || root === 'giglaya.com') return true;
    if (current) {
        if (current === hostname) return true;
        if (registrableDomain(current) === root) return true;
    }
    return false;
}

export function parseReferrer(referrer: string | null | undefined, current: string | null = currentHostname()): ReferrerClass {
    if (!referrer || !referrer.trim()) return { kind: 'empty' };

    let hostname: string;
    try {
        const url = new URL(referrer.trim());
        if (!url.hostname) return { kind: 'unparseable' };
        hostname = normalizeHostname(url.hostname);
    } catch {
        return { kind: 'unparseable' };
    }

    if (!hostname) return { kind: 'unparseable' };
    if (isInternalRegistrableDomain(hostname, current)) return { kind: 'internal', hostname };

    const ai = AI_HOST_TO_SOURCE[hostname];
    if (ai) return { kind: 'ai', source: ai, hostname };

    if (GOOGLE_SEARCH_HOST.test(hostname)) {
        return { kind: 'search', source: 'google', hostname };
    }
    const search = SEARCH_HOST_TO_SOURCE[hostname];
    if (search) return { kind: 'search', source: search, hostname };

    const social = SOCIAL_HOST_TO_SOURCE[hostname];
    if (social) return { kind: 'social', source: social, hostname };

    return { kind: 'referral', source: registrableDomain(hostname), hostname };
}

export function normalizeLlmUtmSource(source: string | null | undefined): string | null {
    if (!source) return null;
    return LLM_SOURCE_ALIASES[source.toLowerCase().trim()] ?? null;
}
